import React, {FC} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SimpleDropdown} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  BlockRecipientDropdownItem,
  DestroyConversationDropdownItem,
  ReportPhishingDropdownItem,
} from '../viewer/conversation/actions/more/conversationsMoreDropdown';
import {
  ConversationsTrashButtonProps,
  ConversationsTrashCoordinatorModesEnum,
} from '../viewer/conversation/actions/trash/conversationsTrashCoordinator';
import {ConversationsTrashCoordinatorContainer} from '../viewer/conversation/actions/trash/conversationsTrashCoordinatorContainer';
import {InboxesTopbarTooltipProps, makeInboxesTopbarSpamTooltipProps} from './inboxesTopbarCommon';
import {InboxesTopbarMainButton, InboxesTopbarMainButtonChevronProps} from './inboxesTopbarMainButton';

/*
 * Props.
 */

interface InboxesTopbarMainSpamProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  recipient: VisualRecipientModel | undefined;
  areAllInternal: boolean;
  contextNamespaces: ReadonlyArray<string>;
  onReportPhishingClick: (() => void) | undefined;
  onBlockRecipientClick: (() => void) | undefined;
  onUnblockRecipientClick: (() => void) | undefined;
  onDestroyClick: (() => void) | undefined;
}

/*
 * Component.
 */

export const InboxesTopbarMainSpam: FC<InboxesTopbarMainSpamProps> = (props) => {
  const theme = useUserTheme();
  const {onReportPhishingClick, onBlockRecipientClick, onUnblockRecipientClick, onDestroyClick} = props;

  const renderDropdown = (
    spamProps: ConversationsTrashButtonProps,
    popoverProps: PopoverTransferProps,
    tooltipProps: InboxesTopbarTooltipProps,
  ) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SimpleDropdown {...popoverProps}>
      <DropdownItem
        isDisabled={!spamProps.permissionResult.isAllowed}
        onSelect={() => {
          spamProps.onClick();
          onUnblockRecipientClick?.();
        }}
      >
        <DropdownItemIcon
          iconName="inboxSmall"
          label={tooltipProps.mainButton}
          color={theme.greys.shade80}
          iconSize={16}
        />
        <DropdownItemContent>
          {props.recipient && props.recipient.isSpammer ? (
            <Trans id="conversation-actions-restoreandunblock">Restore and unblock sender</Trans>
          ) : (
            tooltipProps.mainButton
          )}
        </DropdownItemContent>
      </DropdownItem>
      <ReportPhishingDropdownItem
        contextNamespaces={props.contextNamespaces}
        areAllInternal={props.areAllInternal}
        onReportPhishingClick={onReportPhishingClick}
        hasIcon
      />
      {props.recipient && (
        <BlockRecipientDropdownItem
          recipient={props.recipient}
          cellsContext={props.cellsContext}
          onBlockRecipientClick={onBlockRecipientClick}
          onUnblockRecipientClick={onUnblockRecipientClick}
          hasIcon
        />
      )}
      <DestroyConversationDropdownItem
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        conversationsInboxes={props.conversationsInboxes}
        onDestroyClick={onDestroyClick}
      />
    </SimpleDropdown>
  );

  const renderButton = (trashProps: ConversationsTrashButtonProps) => {
    const tooltipProps = makeInboxesTopbarSpamTooltipProps(trashProps, props);

    const chevronProps: InboxesTopbarMainButtonChevronProps = {
      renderDropdown: (popoverProps) => renderDropdown(trashProps, popoverProps, tooltipProps),
      tooltipFirstLine: <Trans id="conversation-topbar-more">More options</Trans>,
    };
    const icon = trashProps.areAllSpam ? (
      <Icon color={theme.palette.purple.shade40} name="spamFilled" />
    ) : (
      <Icon name="spam" />
    );

    return (
      <InboxesTopbarMainButton
        permissionResult={trashProps.permissionResult}
        onClick={trashProps.onClick}
        keyboardAction={KeyboardActionsEnum.SPAM}
        tooltipFirstLine={tooltipProps.firstLine || tooltipProps.mainButton}
        tooltipSecondLine={tooltipProps.secondLine}
        chevronProps={chevronProps}
        icon={icon}
        conversations={props.conversations}
        cellsContext={props.cellsContext}
      >
        {tooltipProps.mainButton}
      </InboxesTopbarMainButton>
    );
  };

  return (
    <ConversationsTrashCoordinatorContainer
      panelId={props.panelId}
      cellsContext={props.cellsContext}
      inboxesParameters={props.inboxesParameters}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      mode={ConversationsTrashCoordinatorModesEnum.SPAM}
      renderButton={renderButton}
      skipTopbarPage
    />
  );
};
